import type { SemanticModel } from '@aibi/contracts';
import { validateSemanticModel } from '@aibi/semantic-model';
import { ok, type AppError } from '@aibi/shared';
import type { QueryApi } from './query-api';
import type { SemanticModelCatalog } from './semantic-model-catalog';

/** Catalogo combinado que ademas sabe que modelos tienen ejecucion real. */
export interface CompositeModelCatalog extends SemanticModelCatalog {
  isLive(modelId: string): boolean;
}

export interface CompositeModelCatalogOptions {
  readonly demo: SemanticModelCatalog;
  readonly api: QueryApi;
}

/**
 * Une los modelos ejecutables del servidor (primero) con los de demostracion. Ante ids
 * repetidos prevalece el modelo del servidor.
 *
 * Si el servidor no responde, el listado incluye los modelos demo y `unavailable()`
 * devuelve el error para que la UI lo muestre: nunca se presenta en silencio. Los modelos
 * del servidor ya conocidos siguen siendo ejecutables (sus consultas fallan con su propio
 * error en lugar de caer al preview) y una respuesta antigua no pisa a una mas reciente.
 */
export function createCompositeModelCatalog({
  demo,
  api,
}: CompositeModelCatalogOptions): CompositeModelCatalog {
  let live = new Map<string, SemanticModel>();
  let issue: AppError | null = null;
  let latest = 0;

  return {
    isLive: (modelId) => live.has(modelId),
    unavailable: () => issue,
    async list() {
      const request = ++latest;
      const [server, demoList] = await Promise.all([api.models(), demo.list()]);
      if (request === latest) {
        if (server.ok) {
          const next = new Map<string, SemanticModel>();
          for (const model of server.value) {
            if (validateSemanticModel(model).ok) next.set(model.id, model);
          }
          live = next;
          issue = null;
        } else {
          issue = server.error;
        }
      }
      if (!demoList.ok) return live.size > 0 ? ok(summaries(live)) : demoList;
      return ok([
        ...summaries(live),
        ...demoList.value
          .filter((summary) => !live.has(summary.id))
          .map((summary) => ({ ...summary, demo: true })),
      ]);
    },
    load(modelId) {
      const model = live.get(modelId);
      return model ? Promise.resolve(ok(model)) : demo.load(modelId);
    },
  };
}

const summaries = (models: ReadonlyMap<string, SemanticModel>) =>
  [...models.values()].map((model) => ({
    id: model.id,
    name: model.name,
    description: model.description ?? null,
  }));
